import React, { useEffect, useMemo, useState } from "react";
import { Table, message, Input } from "antd";
import { Search } from "lucide-react";
import { delay, motion } from "framer-motion";
import { DollarSign, Trophy, Users } from "lucide-react";
import ManagerLayout from "../../layouts/ManagerLayout";
import API from "../../utils/api";
import Loader from "../../components/Loader";
import BulkUploadButton from "../../components/BulkUploadButton";

const TtlManager = ({ isActive = true }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(true);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const [enrollmentSearch, setEnrollmentSearch] = useState("");
  const [debouncedEnrollment, setDebouncedEnrollment] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedEnrollment(enrollmentSearch.trim());
    }, 400);

    return () => clearTimeout(t);
  }, [enrollmentSearch]);

  const fetchLeaderboard = async (
    page = 1,
    pageSize = 500,
    enrollment = "",
  ) => {
    setLoading(true);
    try {
      const params = { page, limit: pageSize };

      if (enrollment) params.enrollment = enrollment; // ✅ query param

      const res = await API.get("/manager/ttl/leaderboard", { params });

      setRows(res.data.leaderboard || []);
      setTotal(res.data.total || null);
      setPagination({
        current: page,
        pageSize,
        total: res.data.total || 0,
      });
    } catch (error) {
      console.error("Fetch leaderboard error:", error);
      message.error("Failed to fetch leaderboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isActive) return;
    fetchLeaderboard(1, pagination.pageSize, debouncedEnrollment);
  }, [isActive, debouncedEnrollment]);

  const columns = useMemo(
    () => [
      {
        title: "Rank",
        dataIndex: "rank",
        key: "rank",
        render: (rank) => {
          let style = "";

          if (rank === 1) {
            style =
              "bg-yellow-100 text-yellow-700 font-bold px-3 py-1 rounded-full";
          } else if (rank === 2) {
            style =
              "bg-gray-200 text-gray-700 font-bold px-3 py-1 rounded-full";
          } else if (rank === 3) {
            style =
              "bg-orange-100 text-orange-700 font-bold px-3 py-1 rounded-full";
          } else {
            style = "text-gray-700 font-semibold";
          }

          return <span className={`text-lg ${style}`}>#{rank}</span>;
        },
      },
      {
        title: "Enrollment",
        dataIndex: "enrollment",
        key: "enrollment",
      },
      {
        title: "Manager",
        dataIndex: "manager",
        key: "manager",
      },
      // {
      //   title: "Join Date",
      //   dataIndex: "joinDate",
      //   key: "joinDate",
      //   render: (date) => (date ? new Date(date).toLocaleDateString() : "N/A"),
      // },
      {
        title: "Launch (IN)",
        dataIndex: "launchIn",
        key: "launchIn",
      },
      {
        title: "Sales (IN)",
        dataIndex: "salesIn",
        key: "salesIn",
        render: (value) =>
          typeof value === "number"
            ? value.toFixed(2)
            : Number(value || 0).toFixed(2),
      },
      {
        title: "Launch (AE)",
        dataIndex: "launchAe",
        key: "launchAe",
      },
      {
        title: "Sales (AE)",
        dataIndex: "salesAe",
        key: "salesAe",
        render: (value) =>
          typeof value === "number"
            ? value.toFixed(2)
            : Number(value || 0).toFixed(2),
      },
      {
        title: "Total Sales",
        dataIndex: "totalSale",
        key: "totalSale",
        render: (value) =>
          typeof value === "number"
            ? value.toFixed(2)
            : Number(value || 0).toFixed(2),
      },
      {
        title: "Prize",
        dataIndex: "prize",
        key: "prize",
        render: (val) => val || "—",
      },
    ],
    [],
  );

  // Top cards data
  const sortedRows = [...rows].sort((a, b) => a.rank - b.rank);

  const totalSales =
    rows.length > 0
      ? Number(rows[0].salesIn || 0) + Number(rows[0].salesAe || 0)
      : 0;

  const formattedTotalSales = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(totalSales);
  const participants = total;
  const topPerformer =
    sortedRows.find((r) => Number(r.rank) === 1)?.enrollment ||
    sortedRows?.[0]?.enrollment ||
    "N/A";

  const handleHoverShadow = (e, shadow) => {
    e.currentTarget.style.boxShadow = shadow;
  };

  return loading ? (
    <Loader />
  ) : (
    <ManagerLayout>
      <div className="px-6 space-y-6 bg-white/2">
        {/* ===== Top Cards (Easyship style) ===== */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {/* Total Sales */}
          <motion.div
            initial={{ opacity: 0, y: 18, x: 400 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center p-4 rounded-xl border bg-white space-x-4 w-full transition duration-300 hover:cursor-pointer"
            style={{ transition: "box-shadow 0.3s ease" }}
            onMouseEnter={(e) =>
              handleHoverShadow(e, "0 4px 12px rgba(99, 102, 241, 0.35)")
            }
            onMouseLeave={(e) => handleHoverShadow(e, "none")}
          >
            <div className="bg-indigo-100 rounded-full p-3">
              <DollarSign className="text-indigo-600 w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Sales</p>
              <p className="text-xl font-semibold text-gray-800">
                {formattedTotalSales}
              </p>
            </div>
          </motion.div>

          {/* Top Performer */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="flex items-center p-4 rounded-xl border bg-white space-x-4 w-full transition duration-300 hover:cursor-pointer"
            style={{ transition: "box-shadow 0.3s ease" }}
            onMouseEnter={(e) =>
              handleHoverShadow(e, "0 4px 12px rgba(250, 204, 21, 0.45)")
            }
            onMouseLeave={(e) => handleHoverShadow(e, "none")}
          >
            <div className="bg-yellow-100 rounded-full p-3">
              <Trophy className="text-yellow-600 w-6 h-6" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-gray-600">Top Performer</p>
              <p className="text-xl font-semibold text-gray-800 truncate max-w-[220px]">
                {topPerformer}
              </p>
            </div>
          </motion.div>

          {/* Participants */}
          <motion.div
            initial={{ opacity: 0, y: 18, x: -200 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center p-4 rounded-xl border bg-white space-x-4 w-full transition duration-300 hover:cursor-pointer"
            style={{ transition: "box-shadow 0.3s ease" }}
            onMouseEnter={(e) =>
              handleHoverShadow(e, "0 4px 12px rgba(16, 185, 129, 0.35)")
            }
            onMouseLeave={(e) => handleHoverShadow(e, "none")}
          >
            <div className="bg-green-100 rounded-full p-3">
              <Users className="text-green-600 w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Participants</p>
              <p className="text-xl font-semibold text-gray-800">
                {participants}
              </p>
            </div>
          </motion.div>
        </div>

        {/* ===== Leaderboard ===== */}
        <motion.div
          initial={{ opacity: 0, y: 200 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="bg-white rounded-2xl shadow-md border p-6"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <motion.h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 hidden sm:flex">
              <motion.span
                className="bg-white"
                initial={{ opacity: 0, x: -40 }}
                animate={{ opacity: 1, x: 210 }}
                transition={{ duration: 0.45, delay: 0.5 }}
              >
                ✈
              </motion.span>
              <motion.span
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: -35 }}
                transition={{ duration: 0.45, delay: 0.5 }}
              >
                Sales Leaderboard
              </motion.span>
            </motion.h2>

            <div className="flex items-center gap-3 justify-end">
              <Input
                allowClear
                value={enrollmentSearch}
                onChange={(e) => setEnrollmentSearch(e.target.value)}
                placeholder="Search by enrollment"
                className="w-[220px] sm:w-[260px] rounded-lg"
                prefix={<Search className="w-4 h-4 text-gray-400" />}
                onPressEnter={() =>
                  fetchLeaderboard(
                    1,
                    pagination.pageSize,
                    enrollmentSearch.trim(),
                  )
                }
              />

              <BulkUploadButton
                onSuccess={() =>
                  fetchLeaderboard(1, pagination.pageSize, debouncedEnrollment)
                }
              />
            </div>
          </div>

          <Table
            bordered
            dataSource={rows}
            columns={columns}
            rowClassName={(record) => {
              if (record.rank === 1) return "bg-yellow-50";
              if (record.rank === 2) return "bg-gray-50";
              if (record.rank === 3) return "bg-orange-50";
              return "";
            }}
            rowKey="_id"
            loading={loading}
            pagination={pagination}
            onChange={(pag) =>
              fetchLeaderboard(pag.current, pag.pageSize, debouncedEnrollment)
            }
            scroll={{ x: "max-content" }}
            locale={{
              emptyText:
                'No sellers added yet. Click "Bulk Upload" to get started.',
            }}
          />
        </motion.div>
      </div>
    </ManagerLayout>
  );
};

export default TtlManager;
